import { Component, ElementRef, HostListener, inject, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { DecimalPipe, NgForOf, NgIf, NgOptimizedImage, NgStyle } from "@angular/common";
import { finalize, from, Observable, of, Subscription, switchMap } from "rxjs";
import {tap} from "rxjs/operators";
import { TranslateModule, TranslateService } from "@ngx-translate/core";

// Services
import { CurrentTenantService } from "../../../../services/front/current-tenant.service";
import { FunctionalCapacitiesService, FunctionalCapacityForm, FunctionalCapacityGroupDiagram } from "../../../../services/back/functional-capacities.service";
import { SnackbarService } from "../../../../services/front/snackbar.service";
import { RightSliderService } from "../../../../services/front/right-slider.service";
import { NewApplicationService } from "../../../../services/back/new-application.service";

// Components and modules
import { DesignSystemModule } from "../../../design-system/design-system.module";
import { DiagramBreadcrumbModule } from "../diagram-breadcrumb/diagram-breadcrumb.module";
import { LoadingProgressComponent } from "../diagram-infrastructure/three/loadingBar";
import { MatRipple } from "@angular/material/core";
import { SnackbarModule } from "../../../global/snackbar/snackbar.module";
import { ApplicationDetailComponent, ApplicationDetailInput } from "../../applications/application-detail/application-detail.component";

// Engine and models
import { CapacityDiagramEngine } from "./engine/capacity-diagram-engine";
import { ApplicationGeneric, ApplicationInstance } from "../../../../services/model/new-application.model";
import {CanvasTransformService} from "./system/canvas-transform.service";
import {ButtonInteractionService} from "./system/button-interaction.service";
import {ScrollbarService} from "./system/canvas-scrollbar.service";
import {DialogManagerService} from "./system/dialog-manager.service";
import {ButtonInfo, CanvasDimensions} from "./system/interfaces";
import {clearImageCache} from "./engine/draw-capacities-app";


@Component({
	selector: 'app-diagram-capacity',
	imports: [
		DecimalPipe,
		DesignSystemModule,
		DiagramBreadcrumbModule,
		LoadingProgressComponent,
		MatRipple,
		NgForOf,
		NgIf,
		TranslateModule,
		NgOptimizedImage,
		SnackbarModule,
		NgStyle,
	],
	templateUrl: './diagram-capacity.component.html',
	styleUrl: './diagram-capacity.component.scss'
})
export class DiagramCapacityComponent implements OnInit, OnDestroy {

	@ViewChild('canvas') canvas!: ElementRef<HTMLCanvasElement>;
	@ViewChild('canvasContainer', { static: true }) canvasContainer: ElementRef<HTMLElement>;

	// Engine and core properties
	private diagramEngine: CapacityDiagramEngine;
	private resizeTimeout: any;
	private gridSize = 30;
	private ctx: CanvasRenderingContext2D;
	private pixelRatio: number;

	// State flags
	protected isData: boolean = false;
	protected isNotData: boolean = false;
	private isDragging: boolean = false;
	private lastMouseX: number = 0;
	private lastMouseY: number = 0;
	private lastZoomLevel: number = 0;
	private imageUpdateTimeout: any;
	private init: boolean = true;

	// Data properties
	tenantId: string;
	_loading: boolean = false;
	_refreshing: boolean = false;
	isEmpty: boolean = false;
	data: FunctionalCapacityGroupDiagram[] = [];
	application: ApplicationInstance[] = [];
	filteredApplications: ApplicationInstance[] = [];

	// Subscriptions
	subscription: Subscription = new Subscription();

	// Injected services
	private currentTenantService = inject(CurrentTenantService);
	private functionalCapacitiesService = inject(FunctionalCapacitiesService);
	private snackbarService = inject(SnackbarService);
	private translate = inject(TranslateService);
	private rightSliderService = inject(RightSliderService);
	private applicationService = inject(NewApplicationService);

	// Refactored services
	private canvasTransformService = inject(CanvasTransformService);
	private buttonInteractionService = inject(ButtonInteractionService);
	private scrollbarService = inject(ScrollbarService);
	private dialogManagerService = inject(DialogManagerService);

	ngOnInit() {
		this.setupSubscriptions();
		this.buttonInteractionService.setGridSize(this.gridSize);
		this.initializeLastZoomLevel();
	}

	private setupSubscriptions(): void {
		// Tenant changes
		this.subscription.add(
			this.currentTenantService.getCurrentTenantIdChanges()
				.pipe(tap(tenantId => this.tenantId = tenantId))
				.subscribe(() => this.initialize())
		);

		// Transform changes
		this.subscription.add(
			this.canvasTransformService.transform$.subscribe(async (transform) => {
				this.updateCanvasTransform(transform);

				// Gérer la mise à jour des images de manière plus intelligente
				this.handleImageResolutionUpdate(transform.scale);
			})
		);

		// Popup and tooltip states
		this.subscription.add(
			this.buttonInteractionService.popup$.subscribe(popupState => {
				this.handlePopupStateChange(popupState);
			})
		);

		this.subscription.add(
			this.buttonInteractionService.tooltip$.subscribe(tooltipState => {
				this.handleTooltipStateChange(tooltipState);
			})
		);
	}

	private handleImageResolutionUpdate(currentScale: number): void {

		const SIGNIFICANT_ZOOM_CHANGE = 0.3;
		const UPDATE_DELAY = 500;

		const scaleChange = Math.abs(currentScale - this.lastZoomLevel);

		if (this.diagramEngine && this.lastZoomLevel && scaleChange > SIGNIFICANT_ZOOM_CHANGE) {
			if (this.imageUpdateTimeout) {
				clearTimeout(this.imageUpdateTimeout);
			}

			this.imageUpdateTimeout = setTimeout(async () => {
				try {
					await this.diagramEngine.updateImageResolution(currentScale);
					this.lastZoomLevel = currentScale;
				} catch (error) {
					console.warn('Erreur lors de la mise à jour de la résolution des images:', error);
				}
			}, UPDATE_DELAY);

		} else if (!this.lastZoomLevel) {
			this.lastZoomLevel = currentScale;
		}
	}

	initialize(): void {
		this.subscription.add(
			this.switchLoading()
				.pipe(
					switchMap(() => this.functionalCapacitiesService.getFunctionalCapacityDiagram(this.tenantId)),
					tap((data) => this.setFunctionalCapacities(data)),
					switchMap(() => this.applicationService.getAllApplicationInstance(this.tenantId)),
					tap(applications => this.setApplication(applications)),
					finalize(() => this.switchLoading())
				)
				.subscribe(() => this.setCreateSchema())
		);
	}

	private setFunctionalCapacities(data: FunctionalCapacityGroupDiagram[]): void {
		this.data = data;
	}

	private setApplication(applications: ApplicationInstance[]): void {
		this.application = applications;
		this.filteredApplications = [...applications];
	}

	private initializeLastZoomLevel(): void {
		this.lastZoomLevel = this.canvasTransformService.getTransform().scale;
	}

	private switchLoading(): Observable<{}> {
		this._loading = !this._loading;
		return of({});
	}

	setCreateSchema(): void {
		if (this.data.length < 1) {
			this.isNotData = true;
			return;
		}

		this.isData = true;
		this.setupDiagramEngine();
		this.setupEventListeners();
		this.initializeCanvas();
	}

	private setupDiagramEngine(): void {
		const canvas = this.canvas.nativeElement;
		const container = this.canvasContainer.nativeElement;
		this.ctx = canvas.getContext('2d', { willReadFrequently: true }) as CanvasRenderingContext2D;
		this.pixelRatio = window.devicePixelRatio || 1;

		if (this.data.length > 20) {
			this.gridSize = 25;
		}

		this.diagramEngine = new CapacityDiagramEngine(
			this.ctx,
			canvas,
			container,
			this.registerButton.bind(this),
			this.translate,
			this.data,
			this.gridSize,
			this.canvasTransformService.getTransform().scale,
		);
	}

	private initializeCanvas(): void {
		const container = this.canvasContainer.nativeElement;
		const dimensions = this.diagramEngine.getDimensions();

		const canvasDimensions: CanvasDimensions = {
			containerWidth: container.clientWidth,
			containerHeight: container.clientHeight,
			canvasWidth: dimensions.width / this.pixelRatio,
			canvasHeight: dimensions.height / this.pixelRatio
		};

		this.canvasTransformService.setDimensions(canvasDimensions, this.data.length, this.gridSize);
		if (this.init) {
			this.canvasTransformService.setInitialPosition(this.data.length, this.gridSize);
			this.init = false;
		}
		this.scrollbarService.setupEventListeners(container);
		this.scrollbarService.updateScrollbars();

		this.diagramEngine.render(0, 0);
	}

	private setupEventListeners(): void {
		const canvas = this.canvas.nativeElement;
		const container = this.canvasContainer.nativeElement;

		canvas.addEventListener('mousedown', this.handleMouseDown);
		window.addEventListener('mousemove', this.handleMouseMove);
		window.addEventListener('mouseup', this.handleMouseUp);
		container.addEventListener('wheel', this.handleWheel, { passive: false });
		canvas.addEventListener('click', this.handleClick);
	}

	private removeEventListeners(): void {
		const canvas = this.canvas?.nativeElement;
		const container = this.canvasContainer?.nativeElement;

		if (canvas) {
			canvas.removeEventListener('mousedown', this.handleMouseDown);
			canvas.removeEventListener('click', this.handleClick);
		}

		window.removeEventListener('mousemove', this.handleMouseMove);
		window.removeEventListener('mouseup', this.handleMouseUp);

		if (container) {
			container.removeEventListener('wheel', this.handleWheel);
		}

		this.scrollbarService.removeEventListeners();
	}

	// Event Handlers
	private handleMouseDown = (e: MouseEvent): void => {
		this.isDragging = true;
		this.lastMouseX = e.clientX;
		this.lastMouseY = e.clientY;
	};

	private handleMouseMove = (e: MouseEvent): void => {
		if (this.isDragging) {
			const deltaX = e.clientX - this.lastMouseX;
			const deltaY = e.clientY - this.lastMouseY;

			this.canvasTransformService.pan(deltaX, deltaY);

			this.lastMouseX = e.clientX;
			this.lastMouseY = e.clientY;
		}

		this.updateHoverState(e);
	};

	private handleMouseUp = (): void => {
		if (this.isDragging) {
			this.canvas.nativeElement.style.cursor = 'grab';
		}
		this.isDragging = false;
	};

	private handleWheel = (e: WheelEvent): void => {
		this.buttonInteractionService.closePopups();
		e.preventDefault();

		if (e.ctrlKey) {
			const containerRect = this.canvasContainer.nativeElement.getBoundingClientRect();
			this.canvasTransformService.zoomToPoint(
				-e.deltaY * 0.01,
				e.clientX,
				e.clientY,
				containerRect
			);
		} else if (e.shiftKey) {
			this.canvasTransformService.scroll(0, e.deltaY, true);
		} else {
			this.canvasTransformService.scroll(e.deltaX, e.deltaY);
		}
	};

	private handleClick = (e: MouseEvent): void => {
		this.handleCanvasClick(e);

		const target = e.target as HTMLElement;
		const isCanvas = this.canvas.nativeElement.contains(target);
		const isPopupGroup = document.querySelector('.popup-group')?.contains(target);
		const isPopup = document.querySelector('.popup')?.contains(target);

		if (!isCanvas && !isPopupGroup && !isPopup) {
			this.buttonInteractionService.closePopups();
		}
	};

	private updateHoverState(e: MouseEvent): void {
		const canvas = this.canvas.nativeElement;
		const rect = canvas.getBoundingClientRect();
		const transform = this.canvasTransformService.getTransform();
		const mouseX = (e.clientX - rect.left) / transform.scale;
		const mouseY = (e.clientY - rect.top) / transform.scale;

		const hoveredButton = this.buttonInteractionService.findButtonAtPosition(mouseX, mouseY);

		// Update tooltip
		this.buttonInteractionService.updateTooltip(
			hoveredButton,
			e,
			rect,
			transform.scale,
			(key: string) => this.translate.instant(key)
		);

		// Update cursor
		canvas.style.cursor = this.buttonInteractionService.getCursorStyle(
			hoveredButton,
			mouseX,
			mouseY,
			this.isDragging
		);

		// Update diagram engine hover state
		if (hoveredButton) {
			this.diagramEngine.setHoveredButton(hoveredButton.id, hoveredButton.type);
		} else {
			this.diagramEngine.setHoveredButton(null, null);
		}
	}

	private handleCanvasClick(e: MouseEvent): void {
		const rect = this.canvas.nativeElement.getBoundingClientRect();
		const transform = this.canvasTransformService.getTransform();
		const clickX = (e.clientX - rect.left) / transform.scale;
		const clickY = (e.clientY - rect.top) / transform.scale;

		const clickedButton = this.buttonInteractionService.findButtonAtPosition(clickX, clickY);

		if (clickedButton) {
			const isPlus = this.buttonInteractionService.isSpecialZoneClick(clickedButton, clickX, clickY);

			const result = this.buttonInteractionService.handleButtonClick(
				clickedButton.id,
				clickedButton.type,
				clickedButton,
				isPlus,
				rect,
				transform.scale
			);

			this.processButtonClickResult(result, clickedButton);
		} else {
			this.buttonInteractionService.closePopups();
		}
	}

	private processButtonClickResult(result: any, button: ButtonInfo): void {
		switch (result.action) {
			case 'showGroupPopup':
			case 'showCapacityPopup':
				// Handled by subscription to popup state
				break;

			case 'openApplicationDrawer':
				this.openApplicationDrawer(result.data.applicationId);
				break;

			case 'openPlusDialog':
				this.onAddApplication(result.data.buttonId);
				break;

			case 'addGroup':
				this.onAddGroup();
				break;

			case 'addCapacity':
				this.onAddCapacity(result.data.button);
				break;
		}
	}

	// Button registration (callback for diagram engine)
	public registerButton(id: string, name: string, type: string, x: number, y: number, width: number, height: number): void {
		this.buttonInteractionService.registerButton(id, name, type, x, y, width, height);
	}

	public clearButtons(): void {
		this.buttonInteractionService.clearButtons();
	}

	// Canvas transform update
	private updateCanvasTransform(transform: any): void {
		if (!this.canvas?.nativeElement) return;

		const canvas = this.canvas.nativeElement;
		canvas.style.transform = `translate(${transform.translateX}px, ${transform.translateY}px) scale(${transform.scale})`;
		canvas.style.transformOrigin = '0 0';
		canvas.style.imageRendering = 'high-quality';

		this.diagramEngine?.updateOffset(0, 0);
		this.scrollbarService.updateScrollbars();
	}

	// Popup and tooltip state handlers
	private handlePopupStateChange(popupState: any): void {
		if (popupState.isGroupVisible) {
			this.buttonInteractionService.updatePopupPosition(
				'.popup-group',
				popupState.groupX,
				popupState.groupY,
				this.canvasContainer.nativeElement
			);
		}

		if (popupState.isCapacityVisible) {
			this.buttonInteractionService.updatePopupPosition(
				'.popup',
				popupState.capacityX,
				popupState.capacityY,
				this.canvasContainer.nativeElement
			);
		}
	}

	private handleTooltipStateChange(tooltipState: any): void {
		if (tooltipState.isVisible) {
			this.buttonInteractionService.updatePopupPosition(
				'.tooltip',
				tooltipState.x,
				tooltipState.y,
				this.canvasContainer.nativeElement
			);
		}
	}

	// Dialog actions
	onAddGroup(): void {
		this.dialogManagerService.openAddGroupDialog(this.tenantId)
			.subscribe(result => {
				this.displayUpdatedSucceed(result.result, "create");
				this.refresh();
			});
		this.buttonInteractionService.closePopups();
	}

	onAddCapacity(button?: ButtonInfo): void {
		const popupState = this.buttonInteractionService.getPopupState();
		const capacityButton = button || popupState.capacityButton;

		if (!capacityButton) return;

		const group = this.data.find(group => group.id === capacityButton.id);

		this.dialogManagerService.openAddCapacityDialog(
			capacityButton.id,
			group?.name || '',
			this.tenantId,
			this.data
		).subscribe(result => {
			this.displayUpdatedSucceed(result.result, "create");
			this.refresh();
		});

		this.buttonInteractionService.closePopups();
	}

	onEditGroup(): void {
		const popupState = this.buttonInteractionService.getPopupState();
		if (!popupState.capacityButton) return;

		const group = this.data.find(group => group.id === popupState.capacityButton?.id);
		if (!group) return;

		this.dialogManagerService.openEditGroupDialog(
			popupState.capacityButton.id,
			group.name,
			group.icon
		).subscribe(result => {
			this.displayUpdatedSucceed(result.result, "edit");
			this.refresh();
		});

		this.buttonInteractionService.closePopups();
	}

	onEditCapacity(): void {
		const popupState = this.buttonInteractionService.getPopupState();
		if (!popupState.capacityButton) return;

		const capacityName = this.findCapacityName(popupState.capacityButton.id);

		this.dialogManagerService.openEditCapacityDialog(
			popupState.capacityButton.id,
			capacityName
		).subscribe(result => {
			this.displayUpdatedSucceed(result.result, "edit");
			this.refresh();
		});

		this.buttonInteractionService.closePopups();
	}

	onAddApplication(buttonId?: string): void {
		const popupState = this.buttonInteractionService.getPopupState();
		const capacityId = buttonId || popupState.capacityButton?.id;

		if (!capacityId) return;

		this.dialogManagerService.openPlusButtonDialog(
			capacityId,
			this.application,
			this.findCapacityApp(capacityId)
		).subscribe(result => {
			this.saveSelectedApplications(result.result, result.base, result.capacityId);
		});

		this.buttonInteractionService.closePopups();
	}

	onDeleteGroup(): void {
		this.dialogManagerService.openDeleteGroupDialog()
			.pipe(
				switchMap(() => {
					const popupState = this.buttonInteractionService.getPopupState();
					const form: FunctionalCapacityForm = {
						functionalCapacityIds: popupState.capacityButton?.id ? [popupState.capacityButton.id] : []
					};
					return this.functionalCapacitiesService.deleteFunctionalCapacities(this.tenantId, form);
				}),
				tap((result) => {
					this.displayUpdatedSucceed(!!result, "del");
					this.buttonInteractionService.closePopups();
					this.refresh();
				})
			).subscribe();
	}

	onDeleteCapacity(): void {
		this.dialogManagerService.openDeleteCapacityDialog()
			.pipe(
				switchMap(() => {
					const popupState = this.buttonInteractionService.getPopupState();
					const form: FunctionalCapacityForm = {
						functionalCapacityIds: popupState.capacityButton?.id ? [popupState.capacityButton.id] : []
					};
					return this.functionalCapacitiesService.deleteFunctionalCapacities(this.tenantId, form);
				}),
				tap((result) => {
					this.displayUpdatedSucceed(!!result, "del");
					this.buttonInteractionService.closePopups();
					this.refresh();
				})
			).subscribe();
	}

	// Utility methods
	private saveSelectedApplications(selectedApplications: ApplicationInstance[], baseApp: ApplicationGeneric[], capacityId: string): void {
		const result = this.comparerListesApplications(baseApp, selectedApplications);

		result.del.forEach(app => {
			const functionalIds = this.getFunctionalIdsForApp(app.id).filter(id => id !== capacityId);
			this.updateApplicationCapacities(app.id, functionalIds);
		});

		result.add.forEach(app => {
			const functionalIds = [...this.getFunctionalIdsForApp(app.applicationId), capacityId];
			this.updateApplicationCapacities(app.applicationId, functionalIds);
		});
	}

	private getFunctionalIdsForApp(appId: string): string[] {
		const functionalIds: string[] = [];

		for (const group of this.data) {
			for (const capacity of group.children) {
				for (const application of capacity.applications) {
					if (appId === application.id) {
						functionalIds.push(capacity.id);
					}
				}
			}
		}

		return functionalIds;
	}

	private updateApplicationCapacities(appId: string, functionalIds: string[]): void {
		const form: FunctionalCapacityForm = { functionalCapacityIds: functionalIds };

		this.functionalCapacitiesService.updateApplicationFunctionalCapacityList(
			this.tenantId,
			appId,
			form
		).subscribe(result => {
			this.displayUpdatedSucceed(result, "add");
			this.refresh();
		});
	}

	comparerListesApplications(
		listeBase: ApplicationGeneric[],
		listeModifiee: ApplicationInstance[]
	): { add: ApplicationInstance[], del: ApplicationGeneric[] } {
		const idsBase = new Set(listeBase.map(app => app.id));
		const idsModifiee = new Set(listeModifiee.map(app => app.applicationId));

		const deleted = listeBase.filter(app => !idsModifiee.has(app.id));
		const added = listeModifiee.filter(app => !idsBase.has(app.applicationId));

		return { del: deleted, add: added };
	}

	findCapacityName(id: string | undefined): string {
		let name: string = '';
		for (const group of this.data) {
			for (const capacity of group.children) {
				if (id === capacity.id) {
					name = capacity.name;
				}
			}
		}
		return name;
	}

	findCapacityApp(id: string | undefined): ApplicationGeneric[] {
		let app: ApplicationGeneric[] = [];
		for (const group of this.data) {
			for (const capacity of group.children) {
				if (id === capacity.id) {
					app = capacity.applications;
				}
			}
		}
		return app;
	}

	displayUpdatedSucceed(updateSucceed: boolean, type: "del" | "edit" | "create" | "add"): void {
		const messageMap = {
			del: updateSucceed ? 'page.application.detail.delete.success' : 'page.application.detail.delete.fail',
			edit: updateSucceed ? 'page.application.detail.update.success' : 'page.application.detail.update.fail',
			create: updateSucceed ? 'page.application.detail.create.success' : 'page.application.detail.create.fail',
			add: updateSucceed ? 'page.application.detail.add.success' : 'page.application.detail.add.fail'
		};

		const snackType = updateSucceed ? undefined : 'danger-snack';
		this.snackbarService.show(this.translate.instant(messageMap[type]), snackType);
	}

	openApplicationDrawer(applicationId: string): void {
		const data: ApplicationDetailInput = { applicationId };
		this.rightSliderService.openComponent(ApplicationDetailComponent, data);
	}

	// Movement controls
	onRecenter(): void {
		this.canvasTransformService.recenter();
	}

	onZoomIn(): void {
		this.canvasTransformService.zoomIn();
	}

	onZoomOut(): void {
		this.canvasTransformService.zoomOut();
	}

	getZoomLevel(): number {
		return this.canvasTransformService.getZoomLevel();
	}

	// Scrollbar getters (for template)
	get shouldShowHorizontalScrollbar(): boolean {
		return this.scrollbarService.shouldShowHorizontalScrollbar(this.data.length);
	}

	get shouldShowVerticalScrollbar(): boolean {
		return this.scrollbarService.shouldShowVerticalScrollbar(this.data.length);
	}

	get horizontalScrollbarStyles(): any {
		return this.scrollbarService.getHorizontalScrollbarStyles();
	}

	get verticalScrollbarStyles(): any {
		return this.scrollbarService.getVerticalScrollbarStyles();
	}

	get horizontalThumbStyles(): any {
		return this.scrollbarService.getHorizontalThumbStyles();
	}

	get verticalThumbStyles(): any {
		return this.scrollbarService.getVerticalThumbStyles();
	}

	// Popup and tooltip getters (for template)
	get popupState() {
		return this.buttonInteractionService.getPopupState();
	}

	get tooltipState() {
		return this.buttonInteractionService.getTooltipState();
	}

	// Refresh and cleanup
	refresh(): void {
		this.buttonInteractionService.closePopups();
		this.clearButtons();

		if (this.canvas?.nativeElement) {
			const canvas = this.canvas.nativeElement;
			const ctx = canvas.getContext('2d');
			if (ctx) {
				ctx.clearRect(0, 0, canvas.width, canvas.height);
			}
		}

		this.isData = false;
		this.isNotData = false;
		this.resetState();
		this.removeEventListeners();
		this.initialize();
		clearImageCache()
	}

	private resetState(): void {
		this.isDragging = false;
		this.lastMouseX = 0;
		this.lastMouseY = 0;
		// this.canvasTransformService.reset();
		this.buttonInteractionService.reset();
		// this.scrollbarService.reset();
	}

	@HostListener('window:resize', ['$event'])
	onWindowResize(event: Event): void {
		if (this.resizeTimeout) {
			clearTimeout(this.resizeTimeout);
		}
		this.resizeTimeout = setTimeout(() => {
			this.handleWindowResize();
		}, 150);
	}

	private handleWindowResize(): void {
		if (!this.canvas?.nativeElement || !this.canvasContainer?.nativeElement) {
			return;
		}

		const container = this.canvasContainer.nativeElement;
		this.canvasTransformService.handleResize(container.clientWidth, container.clientHeight, this.data.length, this.gridSize);
		this.scrollbarService.removeEventListeners();
		this.scrollbarService.setupEventListeners(container);
	}

	ngOnDestroy() {
		this.subscription.unsubscribe();
		this.removeEventListeners();
		this.buttonInteractionService.reset();
		this.scrollbarService.reset();

		if (this.resizeTimeout) {
			clearTimeout(this.resizeTimeout);
		}

		if (this.imageUpdateTimeout) {
			clearTimeout(this.imageUpdateTimeout);
		}
		clearImageCache()
	}
}
